import { createSelector } from "@reduxjs/toolkit";
import { noCase } from "change-case";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  useDynamicFormatString,
  useFormatMessage,
  type FormatMessages,
  type FormatStrings,
} from "@skilitics/localization";
import type { ValueReferenceCycle, ValueReferenceEdge } from "@skilitics/nautilus-core-designer-data";

import {
  getDesignerNodeByIdSelector,
  getManifestSelector,
  getNodeTitleByIdSelector,
  getNodeTypeIdSelector,
  getParentContextByIdSelector,
} from "../../state/index.ts";
import { getNodeValuesCreate } from "../querying/getNodeValues.ts";

/** One step of a loop: a node, and what it reads from the step after it. */
export interface ValueReferenceCycleHop {
  nodeId: number;
  /** Unique within the cycle, so repeated node titles stay tellable apart. */
  nodeTitle: string;
  kind: "reference" | "child-result";
  /** What the node reads, named as the author sees it. Reference hops only. */
  valueTitle?: string;
  /** The slot whose children fold into this node's own result. Child-result hops only. */
  slotLabel?: string;
}

export interface ValueReferenceCycleDescription {
  hops: ValueReferenceCycleHop[];
  /** The node the last hop leads back to — the first hop's node, restated. */
  closesOn: { nodeId: number; nodeTitle: string };
  /**
   * The hops an author can undo: the ones they authored. Always at least one, since a loop of
   * nothing but child-result hops would need a node to be its own ancestor.
   */
  breakable: ValueReferenceCycleHop[];
}

interface Deps {
  getNodeTitle: (nodeId: number) => string | undefined;
  /** The title a node advertises for the value at `property`, by value name or runtime path. */
  getNodeValueTitle: (nodeId: number, property: string) => string | undefined;
  /** Stands in for a node the design can't title. */
  formatUntitledNode: (nodeId: number) => string;
  /** Says a slot in the author's vocabulary, given the node holding it. */
  formatSlotLabel: (ownerNodeId: number, slotName: string) => string;
}

/**
 * Turn a detected cycle into something an author can act on: each node named, each step said in
 * their own vocabulary, and the authored steps marked so the fix can point at one.
 *
 * Deliberately drops `edge.name` for reference hops — it's a dotted path into config or built
 * bindings, which means nothing to the person who has to break the loop.
 */
export const describeValueReferenceCycleCreate =
  (deps: Deps) =>
  (cycle: ValueReferenceCycle): ValueReferenceCycleDescription => {
    const titles = resolveTitles(cycle, deps);
    const titleOf = (nodeId: number) => titles.get(nodeId) ?? deps.formatUntitledNode(nodeId);

    const hops = cycle.edges.map((edge): ValueReferenceCycleHop => {
      const base = { nodeId: edge.fromNodeId, nodeTitle: titleOf(edge.fromNodeId) };
      if (edge.kind === "child-result") {
        return {
          ...base,
          kind: "child-result",
          slotLabel: edge.name ? deps.formatSlotLabel(edge.fromNodeId, edge.name) : undefined,
        };
      }
      return { ...base, kind: "reference", valueTitle: valueTitle(edge, deps) };
    });

    const [first] = cycle.edges;
    return {
      hops,
      closesOn: { nodeId: first.fromNodeId, nodeTitle: titleOf(first.fromNodeId) },
      breakable: hops.filter((hop) => hop.kind === "reference"),
    };
  };

export type DescribeValueReferenceCycle = ReturnType<typeof describeValueReferenceCycleCreate>;

const valueTitle = (edge: ValueReferenceEdge, deps: Deps): string | undefined =>
  edge.property ? (deps.getNodeValueTitle(edge.toNodeId, edge.property) ?? edge.property) : undefined;

/**
 * A title per node in the cycle, made unique: same-titled nodes (two "Number Attribute"s) are
 * numbered. Each one is a link, so the loop is short enough to read and still gettable to.
 */
const resolveTitles = (cycle: ValueReferenceCycle, deps: Deps): Map<number, string> => {
  const nodeIds = [...new Set(cycle.edges.map((edge) => edge.fromNodeId))];
  const plain = new Map(
    nodeIds.map((nodeId) => [nodeId, deps.getNodeTitle(nodeId) ?? deps.formatUntitledNode(nodeId)]),
  );

  const numbered = new Map(plain);
  for (const title of duplicatedTitles(plain)) {
    let ordinal = 0;
    for (const [nodeId, nodeTitle] of plain) {
      if (nodeTitle === title) numbered.set(nodeId, `${title} #${++ordinal}`);
    }
  }
  return numbered;
};

const isShared = (title: string, titles: ReadonlyMap<number, string>) =>
  [...titles.values()].filter((other) => other === title).length > 1;

const duplicatedTitles = (titles: ReadonlyMap<number, string>) =>
  new Set([...titles.values()].filter((title) => isShared(title, titles)));

/**
 * A reference edge names either the descriptor's own name (config references) or its runtime path
 * (built bindings), so match on both and answer with the title the author picked it by.
 */
const getNodeValueTitleSelector = createSelector(
  [getDesignerNodeByIdSelector, getManifestSelector, getParentContextByIdSelector],
  (getById, getManifest, getParentContext) => {
    const getNodeValues = getNodeValuesCreate({ getManifest, getParentContext });
    return (nodeId: number, property: string): string | undefined => {
      try {
        const values = getNodeValues(getById(nodeId));
        return values.find((value) => value.name === property || value.runtimePath === property)?.title;
      } catch {
        // deleted node, or a config the manifest can't read values from mid-edit
        return undefined;
      }
    };
  },
);

/**
 * A cycle can name a node the design can't title: a published cycle reaches nodes that only exist
 * once published, and a node can go mid-edit. Either way that's no title rather than a throw.
 */
const getNodeTitleIfDesignedSelector = createSelector(
  [getNodeTitleByIdSelector],
  (getNodeTitle) =>
    (nodeId: number): string | undefined => {
      try {
        return getNodeTitle(nodeId);
      } catch {
        return undefined;
      }
    },
);

/**
 * The label the node holding the slot gives it, where it gives one. Only that node knows what its
 * own slot means to an author, so the wording comes off its designer manifest rather than a map
 * here of every slot name in the product.
 */
const getSlotLabelSelector = createSelector(
  [getNodeTypeIdSelector, getManifestSelector],
  (getTypeId, getManifest) =>
    (nodeId: number, slotName: string): FormatStrings | undefined => {
      try {
        // a design can outlive the manifest for its type; no manifest is simply no label
        return getManifest(getTypeId(nodeId), { ignoreIfNotFound: true })?.designer.children?.[slotName]?.label;
      } catch {
        return undefined;
      }
    },
);

const describeValueReferenceCycleDepsSelector = createSelector(
  [getNodeTitleIfDesignedSelector, getNodeValueTitleSelector, getSlotLabelSelector],
  (getNodeTitle, getNodeValueTitle, getSlotLabel) => ({ getNodeTitle, getNodeValueTitle, getSlotLabel }),
);

export const useDescribeValueReferenceCycle = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const formatDynamicString = useDynamicFormatString();
  const { getNodeTitle, getNodeValueTitle, getSlotLabel } = useSelector(describeValueReferenceCycleDepsSelector);

  return useMemo(
    () =>
      describeValueReferenceCycleCreate({
        getNodeTitle,
        getNodeValueTitle,
        formatUntitledNode: (nodeId) => formatMessage("untitledNode", { nodeId }),
        formatSlotLabel: (ownerNodeId, slotName) => {
          const label = getSlotLabel(ownerNodeId, slotName);
          // Slot names read well enough spaced out ("evaluationPatterns" → "evaluation patterns"),
          // so a slot only needs a label where the raw name says nothing about what it holds.
          return label ? formatDynamicString(label) : noCase(slotName);
        },
      }),
    [formatDynamicString, formatMessage, getNodeTitle, getNodeValueTitle, getSlotLabel],
  );
};

const formatStrings = {
  untitledNode: {
    en: "element {nodeId}",
  },
} satisfies FormatMessages;
